import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
/* Font Awesome bundled with webpack — avoids CDN/font blocking (icons as squares on localhost) */
import '@fortawesome/fontawesome-free/css/all.min.css'
import './assets/styles/global.css'
import whitelabelBranding from './services/whitelabelBrandingService'
import observabilityAPI from './services/observabilityAPI'
import { startPilotBuildIdWatch } from './services/appBuildId'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'

// Application version (should match backend APP_VERSION)
const APP_VERSION = '10.9.2'

// Initialize error capture for feedback system (captures console errors and network failures)
observabilityAPI.initErrorCapture()

// Sentry will be loaded dynamically if available
let Sentry = null

// Handle chunk loading errors (happens when new deployment changes chunk hashes)
// This auto-reloads the page once to get fresh JavaScript
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('Loading chunk')) {
    const hasReloaded = sessionStorage.getItem('chunk_reload_attempted')
    if (!hasReloaded) {
      sessionStorage.setItem('chunk_reload_attempted', 'true')
      console.warn('🔄 Chunk loading failed, reloading to get fresh assets...')
      window.location.reload()
    } else {
      console.error('❌ Chunk loading failed after reload. Please clear your browser cache.')
      sessionStorage.removeItem('chunk_reload_attempted')
    }
  }
})

// Also handle unhandled promise rejections for dynamic imports
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('Loading chunk')) {
    const hasReloaded = sessionStorage.getItem('chunk_reload_attempted')
    if (!hasReloaded) {
      sessionStorage.setItem('chunk_reload_attempted', 'true')
      console.warn('🔄 Chunk loading failed, reloading to get fresh assets...')
      window.location.reload()
    } else {
      console.error('❌ Chunk loading failed after reload. Please clear your browser cache.')
      sessionStorage.removeItem('chunk_reload_attempted')
    }
    event.preventDefault()
  }
})

// Clear the reload flag on successful app initialization
const clearReloadFlag = () => {
  setTimeout(() => {
    sessionStorage.removeItem('chunk_reload_attempted')
  }, 5000)
}

const app = createApp(App)

// =============================================================================
// Sentry/GlitchTip Error Tracking Integration
// =============================================================================
// Initialize Sentry for error tracking (GlitchTip is Sentry-compatible)
// DSN is set via environment variable: VITE_SENTRY_DSN or VUE_APP_SENTRY_DSN
const SENTRY_DSN = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SENTRY_DSN) || 
                   process.env?.VUE_APP_SENTRY_DSN || 
                   ''

// Initialize Sentry if DSN is configured and package is available
async function initSentry() {
  if (!SENTRY_DSN) {
    console.log('ℹ️ Sentry DSN not configured, error tracking disabled')
    return
  }
  
  try {
    Sentry = await import('@sentry/vue')
    
    Sentry.init({
      app,
      dsn: SENTRY_DSN,
      
      // Environment and release identification
      environment: (typeof import.meta !== 'undefined' && import.meta.env?.MODE) || process.env?.NODE_ENV || 'development',
      release: `praetorian-frontend@${APP_VERSION}`,
      
      // Integrations
      integrations: [
        // Browser tracing for performance monitoring
        Sentry.browserTracingIntegration({
          router,  // Track Vue Router navigation
        }),
      ],
      
      // Performance monitoring (10% of transactions in prod, all in dev)
      tracesSampleRate: (typeof import.meta !== 'undefined' && import.meta.env?.PROD) ? 0.1 : 1.0,
      
      // Track release health
      autoSessionTracking: true,
      
      // Add application context based on URL
      beforeSend(event) {
        // Determine which application based on URL
        const url = window.location.pathname
        let application = 'adi'
        
        if (url.includes('/courses') || url.includes('/lms') || url.includes('/learn')) {
          application = 'lms'
        }
        
        event.tags = event.tags || {}
        event.tags.application = application
        
        // Add current route info
        if (router.currentRoute.value) {
          event.tags.route_name = router.currentRoute.value.name || 'unknown'
          event.contexts = event.contexts || {}
          event.contexts.vue = {
            routeName: router.currentRoute.value.name,
            routePath: router.currentRoute.value.path,
          }
        }
        
        return event
      },
      
      // Ignore common non-errors
      ignoreErrors: [
        'Loading chunk',
        'ChunkLoadError',
        'Network Error',
        'NetworkError',
        'ResizeObserver loop',
      ],
    })
    
    console.log('🔍 Sentry/GlitchTip error tracking initialized')
  } catch (e) {
    console.log('ℹ️ @sentry/vue not available, error tracking disabled')
  }
}

// Call Sentry init (non-blocking)
initSentry()

app.use(router)
app.use(Toast, {
  position: 'top-right',
  timeout: 3000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  rtl: false
})

// Initialize branding system before mounting
async function initializeApp() {
  try {
    // Initialize whitelabel branding system
    // Note: The whitelabel service will auto-detect organization from user/URL
    await whitelabelBranding.initialize()
    
    // Wait for router to be ready
    await router.isReady()
    
    // Mount the app
    app.mount('#app')
    
    // App loaded successfully - clear any chunk reload flags
    clearReloadFlag()
    startPilotBuildIdWatch()
  } catch (error) {
    console.warn('Failed to initialize branding, proceeding with defaults:', error)
    
    // Fallback: mount app with default branding
    await router.isReady()
    app.mount('#app')
    
    // App loaded successfully - clear any chunk reload flags
    clearReloadFlag()
    startPilotBuildIdWatch()
  }
}

// Make whitelabel branding service globally available
app.config.globalProperties.$branding = whitelabelBranding

// Initialize and mount the app
initializeApp()
